import { useTranslations } from 'next-intl';
import { NoticePanel } from '@/components/ui/notice-panel';
import { SUPPORT_EMAIL } from '@/lib/license-proof';

/**
 * The ljuddämpare notice: a suppressor needs no licence of its own, but the
 * customer must prove they hold a weapon it fits before the order can be picked.
 *
 * Shown on the product page, in the cart and on the order confirmation — the
 * same copy each time, because it is one instruction and repeating it verbatim
 * is what stops a customer reading it as three different requirements. The
 * checkout draws the acknowledgement checkbox instead, and links back to this
 * wording.
 *
 * `title` is a prop because the cart heads it differently ("Din kundvagn
 * innehåller en ljuddämpare"): the panel describes the order there rather than
 * the product in front of you.
 *
 * The requirements are a real <ol> rather than "1. … 2. …" inside a sentence —
 * a screen reader announces two items, and the numbering survives a line wrap.
 */
export function LicenseProofNotice({ title }: { title?: string }) {
  const t = useTranslations('product.licenseProof');

  return (
    <NoticePanel title={title ?? t('title')}>
      <span className="block">{t('intro')}</span>
      <span className="mt-2 block font-medium">{t('requirementsHeading')}</span>
      <ol className="mt-1 list-decimal space-y-0.5 pl-5">
        <li>{t('requirementId')}</li>
        <li>{t('requirementLicense')}</li>
      </ol>
      <span className="mt-2 block">
        {t.rich('mailInstruction', {
          email: () => (
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="font-medium text-accent-700 underline underline-offset-2"
            >
              {SUPPORT_EMAIL}
            </a>
          ),
        })}
      </span>
      <span className="mt-2 block">{t('confirmation')}</span>
    </NoticePanel>
  );
}
